import { LegacyOrderDefinition } from '../../helpers';
import {
  AbstractComparatorApiService,
  AddressApiSuccessResponse,
  AddressData,
  CalculateParameters,
  Comparison,
  CustormerBenefitItem,
  IComparatorApiServiceParameters,
} from './';
import { GraphLegacyApiService } from './comparator-graph-legacy-api';
import { GraphCBHealthApi } from './graphql/api/cb-health/graphCBHealthAPI';
import {
  HealthCare_AdditionalCoverage,
  HealthCare_PostcodeValidation,
} from './graphql/api/cb-health/types';
import { GraphSavingsApi } from './graphql/api/savings/graphSavingsAPI';
import { GraphSolarPanelsApi } from './graphql/api/solar-panels/graphSolarPanelsAPI';
import { GraphTravelApi } from './graphql/api/travel/graphTravelAPI';
import {
  FeaturesConnection,
  MasterAgreementStructure,
  OrderResult,
  OrderStatusResult,
} from './home';
import { IProduct, QuoteResponse } from './product';
import { GraphMoneysureApi } from './graphql/api/moneysure/graphMoneysureAPI';
import { GraphOSBusinessCarAPI } from './graphql/api/os-business-car/graphOSBusinessCarAPI';

const tokenSuccessResponse = {
  ok: true,
  status: 200,
  json: async () => ({
    access_token: 'ACCESS_TOKEN_NOT_REAL_TOKEN',
  }),
};
export class GraphComparisonApiService<
  T extends AbstractComparatorApiService,
> extends AbstractComparatorApiService {
  private apiService: T;
  constructor(
    params: IComparatorApiServiceParameters,
    apiService: { new (params: IComparatorApiServiceParameters): T }
  ) {
    super(params);
    this.apiService = new apiService(params);
  }

  getClient(): any {
    return this.apiService.getClient();
  }

  getComparison(id: string): Promise<Comparison> {
    return this.apiService.getComparison(id);
  }

  getQuotePrefill(
    accessToken: string,
    swiftUserId: string,
    swiftProductId: string,
    productType: string
  ): Promise<QuoteResponse | false> | undefined {
    console.log(swiftUserId, swiftProductId, productType, accessToken);
    return undefined;
  }

  getCustomerBenefits(): Promise<CustormerBenefitItem[]> {
    return this.apiService.getCustomerBenefits();
  }

  getOrderDefinition(
    product: IProduct,
    productType: 'cars' | 'energy' | 'energyAuction'
  ): Promise<LegacyOrderDefinition[]> {
    return this.apiService.getOrderDefinition(product, productType);
  }

  validateAddress(
    postcode: string,
    houseNumber: string,
    houseNumberAddition?: string
  ): Promise<AddressApiSuccessResponse> {
    return this.apiService.validateAddress(
      postcode,
      houseNumber,
      houseNumberAddition
    );
  }

  getAddressData(
    postalCode: string,
    houseNumber: string,
    houseNumberAddition?: string
  ): Promise<AddressData> {
    return this.apiService.getAddressData(
      postalCode,
      houseNumber,
      houseNumberAddition
    );
  }
  getHomeFeaturesDetails(
    productId: number | number[],
    after: string | null
  ): Promise<FeaturesConnection> {
    return this.apiService.getHomeFeaturesDetails(productId, after);
  }

  calculateHomeContentPackage(
    calculateParameters: CalculateParameters
  ): Promise<MasterAgreementStructure> {
    return this.apiService.calculateHomeContentPackage(calculateParameters);
  }

  getProducts(
    searchCriteria: object,
    selectedFilters?: object,
    requestOptions?: RequestInit,
    quotePrefillProps?: { swiftUserId: string; swiftProductId: string },
    useOldValidation?: boolean
  ): Promise<IProduct[]> {
    return this.apiService.getProducts(
      searchCriteria,
      selectedFilters,
      requestOptions,
      quotePrefillProps,
      useOldValidation
    );
  }

  orderAffiliateContentPackage(
    transactionReference: string,
    productCode: string,
    affiliateIncentive?: {
      userDesiresIncentive: boolean;
      callName?: string;
      email?: string;
    },
    comparatorName?: string
  ): Promise<OrderResult> {
    return this.apiService.orderAffiliateContentPackage(
      transactionReference,
      productCode,
      affiliateIncentive,
      comparatorName
    );
  }

  sendEmailToCustomer(
    comparisonId: string | number,
    email: string,
    productType: string,
    inactivity: boolean,
    intake: boolean,
    continueCoverage: boolean
  ): Promise<string> {
    return this.apiService.sendEmailToCustomer(
      comparisonId,
      email,
      productType,
      inactivity,
      intake,
      continueCoverage
    );
  }

  sendCustomerData(
    firstName: string,
    email: string,
    productType: string
  ): Promise<string> {
    return this.apiService instanceof GraphCBHealthApi
      ? this.apiService.sendCustomerData(firstName, email, productType)
      : Promise.resolve(
          'sendCustomerData is not implemented on this Graph API'
        );
  }

  getCalculationInput(
    transactionReference: string
  ): Promise<MasterAgreementStructure> {
    return this.apiService.getCalculationInput(transactionReference);
  }

  orderStatus(orderId: string): Promise<OrderStatusResult> {
    return this.apiService.orderStatus(orderId);
  }

  getType(): string {
    let retVal = 'UNKNOWN';

    if (this.apiService instanceof GraphLegacyApiService) {
      retVal = 'GraphLegacyApiService';
    }

    return retVal;
  }

  acquireToken(): Promise<Response> {
    return this.apiService instanceof GraphLegacyApiService
      ? this.apiService.acquireToken()
      : (Promise.resolve(tokenSuccessResponse) as Promise<Response>);
  }

  getHeaders(headers: object): Promise<any> {
    return this.apiService instanceof GraphLegacyApiService
      ? this.apiService.getHeaders(headers)
      : (Promise.resolve(tokenSuccessResponse) as Promise<Response>);
  }

  setClient(client: any): void {
    if (
      this.apiService instanceof GraphLegacyApiService ||
      this.apiService instanceof GraphCBHealthApi ||
      this.apiService instanceof GraphTravelApi ||
      this.apiService instanceof GraphSolarPanelsApi ||
      this.apiService instanceof GraphSavingsApi ||
      this.apiService instanceof GraphMoneysureApi ||
      this.apiService instanceof GraphOSBusinessCarAPI
    ) {
      this.apiService.setClient(client);
    }
  }

  getAdditionalCoverages(
    userInputData,
    credentials
  ): Promise<HealthCare_AdditionalCoverage[]> | null {
    if (this.apiService instanceof GraphCBHealthApi) {
      return this.apiService.getAdditionalCoverages(userInputData, credentials);
    }
    return null;
  }

  recalculatePremium(userInputData): Promise<any> | null {
    if (this.apiService instanceof GraphMoneysureApi) {
      return this.apiService.recalculatePremium(userInputData);
    }
    return null;
  }

  validatePostcode(
    postalCodeInput: string
  ): Promise<HealthCare_PostcodeValidation[]> | null {
    if (this.apiService instanceof GraphCBHealthApi) {
      return this.apiService.validatePostcode(postalCodeInput);
    }
    return null;
  }

  getNearbyCareGivers(postalCode, careProviderIds, packageIds) {
    if (this.apiService instanceof GraphCBHealthApi) {
      return this.apiService.getNearbyCareGivers(
        postalCode,
        careProviderIds,
        packageIds
      );
    }
    return null;
  }

  searchCareGivers(query: string, postalCode: string, careGiverType: string) {
    if (this.apiService instanceof GraphCBHealthApi) {
      return this.apiService.searchCareGivers(query, postalCode, careGiverType);
    }
    return null;
  }

  getCareGiverDetails(
    externalSourceName: string,
    postalCode,
    packageIds: string[]
  ) {
    if (this.apiService instanceof GraphCBHealthApi) {
      return this.apiService.getCareGiverDetails(
        externalSourceName,
        postalCode,
        packageIds
      );
    }
    return null;
  }

  checkIncentiveStatus(email: string) {
    if (this.apiService instanceof GraphCBHealthApi) {
      return this.apiService.checkIncentiveStatus(email);
    }
    return null;
  }

  registerIncentive(
    firstName: string,
    prefix: string,
    surname: string,
    email: string,
    postcode: string,
    houseNumber: string,
    houseNumberAddition: string,
    birthDate: string,
    transactionDate: string
  ) {
    if (this.apiService instanceof GraphCBHealthApi) {
      return this.apiService.registerIncentive(
        firstName,
        prefix,
        surname,
        email,
        postcode,
        houseNumber,
        houseNumberAddition,
        birthDate,
        transactionDate
      );
    }
    return null;
  }

  calculateHealthCare(userInputData, credentials) {
    if (this.apiService instanceof GraphCBHealthApi) {
      return this.apiService.calculateHealthCare(userInputData, credentials);
    }
    return null;
  }

  calculateAdditionalCoverages(searchCriteria, selectedFilters, refKey) {
    if (this.apiService instanceof GraphOSBusinessCarAPI) {
      return this.apiService.calculateAdditionalCoverages(
        searchCriteria,
        selectedFilters,
        refKey
      );
    }
    return null;
  }

  getComparatorConfig(variables) {
    if (this.apiService instanceof GraphCBHealthApi) {
      return this.apiService.getComparatorConfig(variables);
    }
    return null;
  }

  getComposedForm(
    formName: string,
    frontEndName: string,
    clientName: string,
    values: any
  ) {
    if (this.apiService instanceof GraphCBHealthApi) {
      return this.apiService.getComposedForm(
        formName,
        frontEndName,
        clientName,
        values
      );
    }
    return null;
  }

  getPolicyCoverageDetails(contractNumbers, sessionId) {
    if (this.apiService instanceof GraphCBHealthApi) {
      return this.apiService.getPolicyCoverageDetails(
        contractNumbers,
        sessionId
      );
    }
    return null;
  }

  getAddedValueBehaviours(contractNumbers, sessionId) {
    if (this.apiService instanceof GraphCBHealthApi) {
      return this.apiService.getAddedValueBehaviours(
        contractNumbers,
        sessionId
      );
    }
    return null;
  }

  getStreetViewImage(
    zipCode: string,
    houseNumber: number,
    houseNumberAddition: string,
    correlationId: string,
    newClientLink?: string
  ) {
    if (this.apiService instanceof GraphSolarPanelsApi) {
      return this.apiService.getStreetViewImage(
        zipCode,
        houseNumber,
        houseNumberAddition,
        correlationId,
        newClientLink
      );
    }
    return null;
  }

  getPanelEstimation(request: any, sessionId: string, newClientLink?: string) {
    if (this.apiService instanceof GraphSolarPanelsApi) {
      return this.apiService.getPanelEstimation(
        request,
        sessionId,
        newClientLink
      );
    }
    return null;
  }

  getSolarPanelsProducts(sessionId: string, newClientLink?: string) {
    if (this.apiService instanceof GraphSolarPanelsApi) {
      return this.apiService.getSolarPanelsProducts(sessionId, newClientLink);
    }
    return null;
  }

  getVatValues(newClientLink?: string) {
    if (this.apiService instanceof GraphSolarPanelsApi) {
      return this.apiService.getVatValues(newClientLink);
    }
    return null;
  }

  getSupplierAdditionalCosts(sessionId: string, newClientLink?: string) {
    if (this.apiService instanceof GraphSolarPanelsApi) {
      return this.apiService.getSupplierAdditionalCosts(
        sessionId,
        newClientLink
      );
    }
    return null;
  }

  getProductsForSupplier(
    supplierId: number,
    sessionId: string,
    newClientLink?: string
  ) {
    if (this.apiService instanceof GraphSolarPanelsApi) {
      return this.apiService.getProductsForSupplier(
        supplierId,
        sessionId,
        newClientLink
      );
    }
    return null;
  }

  getPeregrineUserHash(fields: any) {
    if (this.apiService instanceof GraphCBHealthApi) {
      return this.apiService.getPeregrineUserHash(fields);
    }
    return null;
  }

  getPeregrinePartnerHash(fields: any) {
    if (this.apiService instanceof GraphCBHealthApi) {
      return this.apiService.getPeregrinePartnerHash(fields);
    }
    return null;
  }

  getPeregrineActiveExperiments(fields: any) {
    if (this.apiService instanceof GraphCBHealthApi) {
      return this.apiService.getPeregrineActiveExperiments(fields);
    }
    return null;
  }

  sendPeregrineEvent(body) {
    if (this.apiService instanceof GraphCBHealthApi) {
      return this.apiService.sendPeregrineEvent(body);
    }
    return null;
  }

  checkMembershipStatus(email: string): Promise<any> {
    return this.apiService.checkMembershipStatus(email);
  }

  addEnergyAuction(variables: any) {
    return this.apiService.addEnergyAuction(variables);
  }

  getCarData(licensePlate: string) {
    if (this.apiService instanceof GraphOSBusinessCarAPI) {
      return this.apiService.getCarData(licensePlate);
    }
    return null;
  }
}
